<template>
  <div class="page-editor flex flex-col h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
    <!-- Title -->
    <section class="flex-none bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
      <div class="flex items-center gap-2">
        <input
          v-if="visible"
          ref="titleRef"
          :key="pageKey + '-title'"
          class="flex-1 bg-transparent p-3 pt-4 pl-12 outline-none text-xl md:text-2xl font-semibold text-gray-900 dark:text-white"
          contenteditable="true"
          :data-placeholder="titlePlaceholder || 'Title'"
          :placeholder="titlePlaceholder || 'Title'"
          v-model="title"
          @blur="onTitleInput"
          @keydown.enter.prevent="focusSubtitle"
        >
        <span v-if="currentVersionName" @click="$emit('showVersions')" class="cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors inline-flex items-center gap-1 px-2 py-0.5 mr-3 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 whitespace-nowrap flex-shrink-0">
          <ClockIcon class="w-3 h-3" />
          {{ currentVersionName }}
        </span>
      </div>
      <!-- Subtitle / Artist autocomplete -->
      <div class="relative">
        <input
          v-if="visible"
          ref="subtitleRef"
          :key="pageKey + '-subtitle'"
          class="w-full bg-transparent px-3 pl-12 pb-1 outline-none text-sm md:text-base text-gray-600 dark:text-gray-300"
          contenteditable="true"
          :data-placeholder="subtitlePlaceholder || 'Subtitle'"
          :placeholder="subtitlePlaceholder || 'Subtitle'"
          v-model="subtitle"
          @input="onSubtitleTyping"
          @blur="onSubtitleBlur"
          @keydown="onSubtitleKeyDown"
          autocomplete="off"
        >
        <div v-if="showArtistDropdown" 
          class="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1"
          @mousedown.prevent
        >
          <div v-if="artistLoading" class="px-3 py-2 text-sm text-gray-400">Searching...</div>
          <template v-else>
            <div
              v-for="(item, index) in artistItems"
              :key="item.id || item._id"
              class="px-3 py-2 cursor-pointer text-sm transition-colors"
              :class="[
                artistDropdownIndex === index 
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 font-medium' 
                  : 'hover:bg-indigo-50 dark:hover:bg-indigo-950 text-gray-800 dark:text-gray-200'
              ]"
              @mousedown.prevent="selectArtist(item)"
              @mouseenter="artistDropdownIndex = index"
            >
              {{ item.name }}
            </div>
            <div
              v-if="subtitle && subtitle.trim() && !artistExists"
              class="px-3 py-2 cursor-pointer text-sm border-t border-gray-200 dark:border-gray-700 flex items-center gap-2 transition-colors"
              :class="[
                artistDropdownIndex === artistItems.length 
                  ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100 font-medium' 
                  : 'hover:bg-green-50 dark:hover:bg-green-950 text-gray-800 dark:text-gray-200'
              ]"
              @mousedown.prevent="addNewArtist"
              @mouseenter="artistDropdownIndex = artistItems.length"
            >
              <PlusCircleIcon class="w-4 h-4 text-green-500" />
              Add "{{ subtitle }}" as new artist
            </div>
          </template>
        </div>
      </div>
      <div class="flex items-center gap-2 px-3">
        <input
          v-if="visible"
          ref="mediaLinkRef"
          :key="pageKey + '-mediaLink'"
          class="flex-1 bg-transparent pl-8 outline-none text-sm md:text-[12px] text-gray-600 dark:text-gray-300"
          contenteditable="true"
          :data-placeholder="mediaLinkPlaceholder || 'YouTube, Spotify, etc.'"
          :placeholder="mediaLinkPlaceholder || 'YouTube, Spotify, etc.'"
          v-model="mediaLink"
          @blur="onMediaLinkInput"
          @keydown.enter.prevent="focusContent"
        >
      </div>
      <!-- Tags Input -->
      <div class="px-3 pb-1 border-b border-gray-200 dark:border-gray-700 pt-1 pl-10">
        <TagsViewer 
          v-if="visible"
          :tags="tags" 
          :editable="true"
          @createTag="openTagEditor" 
          @update:tags="onTagsUpdatedFromViewer"
        />
        <TriadTagsEditor ref="tagsEditorModal" />
      </div>
    </section>
    <div class="flex-1 min-h-0 flex flex-col">
      <SongEditor v-model="content" 
              :key="pageKey"
              class="flex-1 min-h-0"
              :border="false"
              :semitone="semitone"
              @transpose="$emit('transpose', $event)"
              @update:semitone="$emit('update:semitone', $event)"
              @error="onError" />
    </div>

  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, nextTick, onMounted, watch, defineExpose, computed } from 'vue'
import { preprocessHtml } from '@/service/textEditor.service';
import {debounce} from '@/service/debounce.js';
import SongEditor from '@/components/editor/SongEditor.vue';
import { PlusCircleIcon, ClockIcon } from '@heroicons/vue/24/outline';
import TagsViewer from '@/components/common/elements/TagsViewer.vue';
import TriadTagsEditor from '@/components/common/TriadTagsEditor.vue';

const props = defineProps({
  visible: { type: Boolean, default: true },
  pageKey: { type: String, required: true },
  titleHtml: { type: String, default: '' },
  subtitleHtml: { type: String, default: '' },
  mediaLinkHtml: { type: String, default: '' },
  contentHtml: { type: String, default: '' },
  titlePlaceholder: { type: String, default: '' },
  subtitlePlaceholder: { type: String, default: '' },
  contentPlaceholder: { type: String, default: '' },
  mediaLink: { type: String, default: '' },
  mediaLinkPlaceholder: { type: String, default: '' },
  autofocus: { type: Boolean, default: true },
  isPublic: { type: Boolean, default: false },
  userAdded: { type: Boolean, default: false },
  currentVersionName: { type: String, default: null },
  tagsData: { type: Array, default: () => [] },
  semitone: { type: Number, default: 0 }
})

const emit = defineEmits(['update:title', 'update:subtitle', 'update:artist', 'update:content', 'update:isPublic', 'update:tags', 'clean:content', 'next', 'focus-prev', 'focus-next', 'error', 'showVersions', 'transpose', 'update:semitone'])

const titleRef = ref(null)
const subtitleRef = ref(null)
const mediaLinkRef = ref(null)
const contentRef = ref('')
// const textEditor = ref(null)
const title = ref(props.titleHtml)
const subtitle = ref(props.subtitleHtml)
const mediaLink = ref(props.mediaLink)
const content = ref(preprocessHtml(props.contentHtml))
const isPublic = ref(props.isPublic)
const tags = ref(props.tagsData || [])

const tagsEditorModal = ref(null);

const onTagsExternal = () => {
  emit('update:tags', tags.value)
}

const onTagsUpdatedFromViewer = (newTags) => {
  tags.value = newTags
  onTagsExternal()
}

const openTagEditor = async () => {
  if (tagsEditorModal.value) {
    const newTags = await tagsEditorModal.value.open(tags.value);
    if (newTags) {
      tags.value = newTags;
      onTagsExternal();
    }
  }
}


// Artist autocomplete
const artistItems = ref([])
const showArtistDropdown = ref(false)
const artistLoading = ref(false)
const selectedArtist = ref(null)
const artistDropdownIndex = ref(-1)

const artistExists = computed(() => {
  if (!subtitle.value || !subtitle.value.trim()) return false
  const q = subtitle.value.trim().toLowerCase()
  return artistItems.value.some(a => a.name.toLowerCase() === q)
})

const searchArtist = debounce(async (query) => {
  if (!query || query.trim() === '') {
    artistItems.value = []
    showArtistDropdown.value = false
    artistDropdownIndex.value = -1
    return
  }
  artistLoading.value = true
  try {
    const response = await fetch(`/api/artist/search?q=${encodeURIComponent(query)}`)
    if (response.ok) {
      const data = await response.json()
      artistItems.value = data || []
      showArtistDropdown.value = true
      artistDropdownIndex.value = -1
    }
  } catch (e) {
    console.error('Error searching artists:', e)
  } finally {
    artistLoading.value = false
  }
}, 300)

const selectArtist = (artist) => {
  if (!artist) return
  const artistId = artist.id || artist._id
  subtitle.value = artist.name
  selectedArtist.value = { id: artistId, name: artist.name }
  emit('update:artist', { id: artistId, name: artist.name })
  showArtistDropdown.value = false
  artistDropdownIndex.value = -1
  onSubtitleInput()
}

const addNewArtist = async () => {
  const name = subtitle.value.trim()
  if (!name) return
  try {
    const response = await fetch('/api/artist/', {
      method: 'POST',
      body: JSON.stringify({ name }),
      headers: { 'Content-Type': 'application/json' }
    })
    if (response.ok) {
      const artist = await response.json()
      const artistId = artist.id || artist._id
      selectedArtist.value = { id: artistId, name: artist.name || name }
      subtitle.value = artist.name || name
      emit('update:artist', { id: artistId, name: artist.name || name })
    } else {
      selectedArtist.value = { name }
      emit('update:artist', { name })
    }
  } catch (e) {
    console.error('Error creating artist:', e)
    selectedArtist.value = { name }
    emit('update:artist', { name })
  } finally {
    showArtistDropdown.value = false
    artistDropdownIndex.value = -1
    onSubtitleInput()
  }
}

const handleSubtitleEnter = async () => {
  if (showArtistDropdown.value) {
    if (artistDropdownIndex.value >= 0 && artistDropdownIndex.value < artistItems.value.length) {
      selectArtist(artistItems.value[artistDropdownIndex.value])
    } else if (artistDropdownIndex.value === artistItems.value.length && subtitle.value && subtitle.value.trim() && !artistExists.value) {
      await addNewArtist()
    } else {
      const exactMatch = artistItems.value.find(
        a => a.name?.toLowerCase() === subtitle.value.trim().toLowerCase()
      )
      if (exactMatch) {
        selectArtist(exactMatch)
      } else if (artistItems.value.length > 0) {
        selectArtist(artistItems.value[0])
      } else if (subtitle.value && subtitle.value.trim()) {
        await addNewArtist()
      }
    }
  }
  showArtistDropdown.value = false
  artistDropdownIndex.value = -1
  focusContent()
}

const onSubtitleKeyDown = (e) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (!showArtistDropdown.value) {
      showArtistDropdown.value = true
      searchArtist(subtitle.value)
      artistDropdownIndex.value = 0
      return
    }
    const totalCount = artistItems.value.length + (subtitle.value && subtitle.value.trim() && !artistExists.value ? 1 : 0)
    if (totalCount > 0) {
      artistDropdownIndex.value = (artistDropdownIndex.value + 1) % totalCount
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (showArtistDropdown.value) {
      const totalCount = artistItems.value.length + (subtitle.value && subtitle.value.trim() && !artistExists.value ? 1 : 0)
      if (totalCount > 0) {
        artistDropdownIndex.value = (artistDropdownIndex.value - 1 + totalCount) % totalCount
      }
    }
  } else if (e.key === 'Enter') {
    e.preventDefault()
    handleSubtitleEnter()
  } else if (e.key === 'Escape') {
    showArtistDropdown.value = false
    artistDropdownIndex.value = -1
  }
}

const onSubtitleBlur = () => {
  onSubtitleInput()
  setTimeout(() => {
    showArtistDropdown.value = false
    artistDropdownIndex.value = -1
  }, 200)
}

const onSubtitleTyping = () => {
  artistDropdownIndex.value = -1
  showArtistDropdown.value = true
  searchArtist(subtitle.value)
}

function onTitleInput() { 
  if (title.value === props.titleHtml) return
  emit('update:title', title.value) 
}
function onSubtitleInput() { 
  if (subtitle.value === props.subtitleHtml) return
  emit('update:subtitle', subtitle.value) 
}
function onMediaLinkInput() { 
  if (mediaLink.value === props.mediaLink) return
  emit('update:mediaLink', mediaLink.value) 
}
function onContentInput(value) { 
  content.value = value;
  emit('update:content', content.value) 
}

function focusSubtitle() {
  nextTick(() => { if (subtitleRef.value) subtitleRef.value.focus() })
}
function focusContent() {
  nextTick(() => { if (contentRef.value) contentRef.value.focus() })
}

const isInitializing = ref(true);

onMounted(() => {
  if (props.autofocus) focusContent()
  setTimeout(() => {
    isInitializing.value = false;
  }, 1000);
})

watch(() => props.pageKey, () => {
  if (props.autofocus) focusContent()
  isInitializing.value = true;
  setTimeout(() => {
    isInitializing.value = false;
  }, 1000);
})

watch(() => props.titleHtml, () => {
  title.value = props.titleHtml
})

watch(() => props.subtitleHtml, () => {
  subtitle.value = props.subtitleHtml
})

watch(() => props.mediaLink, () => {
  mediaLink.value = props.mediaLink
})

watch(() => props.contentHtml, (newVal) => {
  const processed = preprocessHtml(newVal)
  // Only update if content genuinely changed to avoid triggering a setContent()
  // in SongEditor (which would reset the cursor) on every auto-save response.
  if (processed !== content.value) {
    content.value = processed
  }
})

watch(() => props.isPublic, () => {
  isPublic.value = props.isPublic
})

watch(() => props.tagsData, () => {
  tags.value = props.tagsData || []
}, { deep: true })

watch( () => content.value, debounce((newVal) => {
    if (!isInitializing.value && !isResetting.value) {
      onContentInput(newVal)
    }
}, 300), { immediate: true })

const onError = (error) => {
    emit('error', error);
}

const isResetting = ref(false)

watch(() => title.value, (newVal) => {
  if (isResetting.value) return
  if (newVal !== props.titleHtml) {
    emit('update:title', newVal)
  }
})

watch(() => subtitle.value, (newVal) => {
  if (isResetting.value) return
  if (newVal !== props.subtitleHtml) {
    emit('update:subtitle', newVal)
  }
})

watch(() => mediaLink.value, (newVal) => {
  if (isResetting.value) return
  if (newVal !== props.mediaLink) {
    emit('update:mediaLink', newVal)
  }
})

defineExpose({
  reset: () => {
    isResetting.value = true;
    title.value = '';
    subtitle.value = '';
    mediaLink.value = '';
    content.value = '';
    nextTick(() => {
      isResetting.value = false;
    });
  }
});
</script>

<style scoped>
[contenteditable][data-placeholder]:empty:before {
  content: attr(data-placeholder);
  color: rgb(156 163 175);
}


</style>


